/*
 * Kozie — collection page behaviour: the size chip row, and the intro clamp.
 *
 * Both are progressive enhancements. With this file blocked the chips are still
 * links that load a filtered page, and the intro is still clamped by CSS with
 * its toggle hidden — nothing breaks, it just costs a page load and the copy
 * stays short.
 */
(function () {
  if (window.__kozieCollectionBound) return;
  window.__kozieCollectionBound = true;

  /* ---------- the intro copy ---------- */

  function setUpIntro(root) {
    var intro = (root || document).querySelector('[data-kozie-intro]');
    if (!intro || intro.dataset.kozieIntroReady === 'true') return;

    var text = intro.querySelector('.kozie-intro__text');
    var toggle = intro.querySelector('.kozie-intro__toggle');
    if (!text || !toggle) return;

    // Only offer the toggle when there is actually something clamped. A two-line
    // description with a "Read more" under it looks broken.
    if (text.scrollHeight - text.clientHeight < 4) return;

    intro.dataset.kozieIntroReady = 'true';
    toggle.hidden = false;

    toggle.addEventListener('click', function () {
      var open = intro.classList.toggle('kozie-intro--open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? 'Read less' : 'Read more';
    });
  }

  /* ---------- the size chips ---------- */

  function stripStock(url) {
    var next = new URL(url, window.location.origin);
    next.searchParams.delete('filter.v.availability');
    return next;
  }

  function onChipClick(event) {
    var chip = event.target.closest('[data-kozie-size-chip]');
    if (!chip || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    // No facets runtime means no in-place render; let the link do its job.
    if (typeof FacetFiltersForm === 'undefined' || typeof FacetFiltersForm.renderPage !== 'function') return;

    event.preventDefault();

    // A chip carries "In stock" the same way a size checkbox does — unless the
    // shopper has already said they want to see everything.
    var url = FacetFiltersForm.kozieStockOptOut ? stripStock(chip.href) : new URL(chip.href, window.location.origin);

    FacetFiltersForm.renderPage(url.searchParams.toString(), null);
  }

  /* ---------- sizes that cannot be bought, on the big collections ----------
   *
   * 'kozie-size-stock' works this out on the server by walking the collection,
   * but Liquid yields at most 50 products in one pass, so on anything larger it
   * refuses to guess and emits nothing. Correct — a wrong cross-out is worse
   * than none — but it meant the feature was silently off on Baby Girl (57),
   * Baby Boy (56) and All (90), the three pages it matters most on. Measured on
   * Baby Girl: 0/6M, 6/12M and One Size have no stock at all, and all three
   * rendered as ordinary tappable chips.
   *
   * The fix is one deferred request. A size facet only tells the truth once an
   * availability filter is applied — with it, Shopify itself marks the dead
   * values disabled — so we ask for this same collection filtered to in-stock
   * and read its verdict. `page=999` is deliberate: the facets are computed
   * from the whole filtered set rather than the page, so an out-of-range page
   * returns the same answer with an empty grid. Measured on Baby Girl: 321KB
   * and 482ms without it, 122KB and 288ms with, identical verdicts.
   *
   * Runs only where the server left the marker, i.e. never on a collection of
   * 50 or fewer and never when an availability filter is already active. Fails
   * silent: no marks rather than wrong ones.
   */

  var stockCache = {};

  function applyVerdict(scroller, inStock) {
    var chips = scroller.querySelectorAll('[data-kozie-size-chip][data-kozie-size-label]');
    for (var i = 0; i < chips.length; i++) {
      var chip = chips[i];
      var label = chip.getAttribute('data-kozie-size-label');
      if (!label || inStock[label]) continue;
      if (chip.classList.contains('kozie-sizes__chip--on')) continue; /* the size they are on */
      chip.classList.add('kozie-sizes__chip--out');
      chip.setAttribute('aria-disabled', 'true');
      if (!chip.querySelector('[data-kozie-out-note]')) {
        var note = document.createElement('span');
        note.className = 'visually-hidden';
        note.setAttribute('data-kozie-out-note', '');
        note.textContent = ' — out of stock';
        chip.appendChild(note);
      }
    }

    /* Dawn's own size facet, in the bar and in the drawer, so the two controls
       on one page cannot disagree about the same collection — the whole reason
       the stock answer is worked out once and shared. */
    var inputs = document.querySelectorAll('input[name="filter.v.option.size"]');
    for (var j = 0; j < inputs.length; j++) {
      var input = inputs[j];
      if (input.checked || inStock[input.value]) continue;
      input.disabled = true;
      var label2 = input.closest('.facets__label, .mobile-facets__label');
      if (label2) label2.classList.add('disabled');
    }
  }

  /* The section id is what makes this a Section Rendering request rather than a
     whole-page fetch. Liquid passes it when it can, but a page that never wires
     the argument must still work — so fall back to the enclosing
     #shopify-section-<id> wrapper, and then to the grid's own data-id. Any new
     template gets this for free. */
  function resolveSectionId(node) {
    var hint = node.getAttribute('data-kozie-stock-unverified');
    if (hint) return hint;

    var wrapper = node.closest('[id^="shopify-section-"]');
    if (wrapper) return wrapper.id.replace('shopify-section-', '');

    var grid = document.querySelector('#product-grid[data-id]');
    if (grid) return grid.getAttribute('data-id');

    return null;
  }

  function verifyStock(scroller) {
    if (!scroller || scroller.dataset.kozieStockChecked === 'true') return;
    if (!window.fetch || !window.DOMParser) return;
    scroller.dataset.kozieStockChecked = 'true';

    var sectionId = resolveSectionId(scroller);
    if (!sectionId) return;

    var url = new URL(window.location.href);
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', '999');
    var key = url.toString();

    if (stockCache[key]) return applyVerdict(scroller, stockCache[key]);

    fetch(key, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var found = doc.querySelectorAll('input[name="filter.v.option.size"]');
        if (!found.length) throw new Error('no size facet in response');
        var inStock = {};
        for (var i = 0; i < found.length; i++) {
          if (!found[i].disabled) inStock[found[i].value] = true;
        }
        /* Every size in stock means nothing to mark — still cache it. */
        stockCache[key] = inStock;
        applyVerdict(scroller, inStock);
      })
      .catch(function () {
        /* Leave the row exactly as the server rendered it. */
      });
  }

  function scanForUnverified() {
    var scroller = document.querySelector('[data-kozie-stock-unverified]');
    if (scroller) verifyStock(scroller);
  }

  var chipObserver = null;

  function watchChips() {
    var row = document.querySelector('[data-kozie-sizes]');
    if (!row || !window.MutationObserver) return;
    if (chipObserver) chipObserver.disconnect();
    /* facets.js swaps this row's innerHTML on every filter and sort, so the
       marker arrives again with the fresh server answer. */
    chipObserver = new MutationObserver(function () {
      scanForUnverified();
    });
    chipObserver.observe(row, { childList: true });
  }

  /* ---------- orphaned quick-add pop-ups ----------
   *
   * Dawn's ModalDialog moves every <quick-add-modal> out of the grid and into
   * <body> on upgrade. facets.js then replaces #ProductGridContainer.innerHTML
   * on every filter and sort — which destroys the CARDS but not the pop-ups,
   * because they no longer live in the container. Nothing ever removed them.
   *
   * Measured on the live preview, filtering three times on Baby Girl:
   *
   *   fresh load        21 pop-ups in <body>,   0 duplicate ids
   *   after 1 filter    45 pop-ups,            24 duplicate ids
   *   after 2 filters   69 pop-ups,            90 duplicate ids
   *   after clearing    93 pop-ups,           156 duplicate ids
   *
   * +24 pop-ups and ~+50 duplicate ids per interaction, growing for the life of
   * the page. Quick-add still worked — Dawn fetches from the OPENER's
   * data-product-url, so even a stale copy renders the right product, and
   * `document.querySelector` resolving to the stale one was verified harmless
   * today. But it is a leak on the most-used page in the store, it makes the
   * document invalid, and it only works by luck: the moment a pop-up carries
   * any state of its own, the stale copy becomes the wrong answer.
   *
   * This is the same failure kozie-related.js already documents and guards
   * against for the rail. The collection page never had the equivalent.
   *
   * The rule is ownership, not ids: a pop-up in <body> is live if some
   * modal-opener still points at it AND it is the most recent copy of that id.
   * Anything else has no way to be opened and is removed. An OPEN pop-up is
   * never touched, whatever else is true — a shopper is looking at it.
   */
  function sweepOrphanModals() {
    var wanted = {};
    document.querySelectorAll('modal-opener[data-modal]').forEach(function (o) {
      var v = o.getAttribute('data-modal');
      if (v && v.charAt(0) === '#') wanted[v.slice(1)] = true;
    });

    var modals = document.querySelectorAll('body > quick-add-modal[id]');
    var seen = {};
    /* backwards: the LAST copy of an id is the one most recently moved out of
       the freshest markup, so it is the live one */
    for (var i = modals.length - 1; i >= 0; i--) {
      var m = modals[i];
      if (m.hasAttribute('open')) { seen[m.id] = true; continue; }
      if (seen[m.id]) { m.remove(); continue; }   /* an older duplicate */
      if (!wanted[m.id]) { m.remove(); continue; } /* no opener can reach it */
      seen[m.id] = true;
    }
  }

  function watchGridForOrphans() {
    var root = document.getElementById('ProductGridContainer');
    if (!root || !window.MutationObserver) return;
    if (gridObserver) gridObserver.disconnect();
    gridObserver = new MutationObserver(function () { sweepOrphanModals(); });
    gridObserver.observe(root, { childList: true });
  }

  var gridObserver = null;

  function bootStock() {
    watchChips();
    watchGridForOrphans();
    sweepOrphanModals();
    var go = function () { scanForUnverified(); };
    /* Deferred on purpose: this must never compete with first paint. */
    if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 2500 });
    else setTimeout(go, 900);
  }

  document.addEventListener('click', onChipClick);
  document.addEventListener('DOMContentLoaded', function () {
    setUpIntro(document);
    bootStock();
  });

  if (document.readyState !== 'loading') {
    setUpIntro(document);
    bootStock();
  }

  /* The theme editor replaces a section's innerHTML and fires
     shopify:section:load, so [data-kozie-sizes] and the intro are NEW elements
     and the observers bound above are watching detached nodes.

     Measured on the live preview before this was added: after one simulated
     section reload the server had re-emitted the data-kozie-stock-unverified
     marker but nothing acted on it — data-kozie-stock-checked never appeared,
     so sold-out sizes silently stopped being crossed out for the rest of the
     session. Every setting the merchant touches on a collection page triggers
     that reload.

     Both entry points are idempotent — setUpIntro guards on
     data-kozie-intro-ready, verifyStock on data-kozie-stock-checked, and both
     markers live on the elements themselves, so fresh markup re-runs and
     already-processed markup does not. The chip-row observer is disconnected
     before a new one is bound rather than stacking one per edit. The click
     handler is delegated on `document` and is deliberately not re-bound. */
  document.addEventListener('shopify:section:load', function () {
    if (chipObserver) { chipObserver.disconnect(); chipObserver = null; }
    if (gridObserver) { gridObserver.disconnect(); gridObserver = null; }
    setUpIntro(document);
    bootStock();
  });
})();
